import React, { useEffect, useState } from 'react';
import { useSeo } from '../lib/seo';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, KeyRound, AlertTriangle } from 'lucide-react';
import partnerApi, { setPartnerSession } from '../lib/partnerApi';

/**
 * Public page hit by the link emailed to a partner after admin approval.
 * Path: /partners/set-password/:token
 *
 * Lifecycle:
 *   1. Mount → GET /api/partners/set-password/:token to validate + show
 *      which email is being activated.
 *   2. User picks a password → POST /api/partners/set-password/:token
 *   3. On success: token + partner stored, redirect to /partners.
 */
export default function PartnerSetPassword() {
  useSeo('Set Your Password | Trade Partner Area | goLeicestershire');
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [validateError, setValidateError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await partnerApi.get(`/partners/set-password/${token}`);
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled) setValidateError(formatErr(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (password.length < 10) {
      setSubmitError('Password must be at least 10 characters long.');
      return;
    }
    if (password !== confirm) {
      setSubmitError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setSubmitError('');
    try {
      const { data } = await partnerApi.post(`/partners/set-password/${token}`, { password });
      setPartnerSession({ token: data.token, partner: data.partner });
      navigate('/partners', { replace: true });
    } catch (e) {
      setSubmitError(formatErr(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-base min-h-[80vh]" data-testid="partner-set-password-page">
      <Helmet>
        <title>Set your password — Trade Partners</title>
      </Helmet>
      <section className="bg-heritage text-base py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[640px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-3">Trade Partners</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">
            {info?.is_reset ? 'Reset your password' : 'Set your password'}
          </h1>
        </div>
      </section>

      <section className="max-w-[560px] mx-auto px-6 lg:px-12 py-12 md:py-16">
        {loading ? (
          <div className="text-charcoal/60 text-sm" data-testid="set-password-loading">Checking your link…</div>
        ) : validateError ? (
          <div className="text-center py-6" data-testid="set-password-invalid">
            <AlertTriangle size={36} className="text-red-700 mx-auto mb-3" />
            <h2 className="font-display text-2xl mb-2">Link no longer valid</h2>
            <p className="text-charcoal/70 leading-relaxed">{validateError}</p>
            <p className="text-sm text-charcoal/55 mt-6">
              Need a new link? Email <a href="mailto:alerts@goleicestershire.com" className="text-heritage underline">alerts@goleicestershire.com</a>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
            <div className="bg-stone border border-charcoal/10 rounded-2xl p-5 mb-2 text-sm">
              <div className="text-charcoal/55 text-xs uppercase tracking-wider mb-1">{info?.is_reset ? 'Resetting password for' : 'Activating'}</div>
              <div className="font-semibold">{info?.business_name}</div>
              <div className="text-charcoal/70">{info?.contact_name} · <span className="text-charcoal">{info?.email}</span></div>
            </div>
            <div className="flex items-center gap-2 text-sm text-charcoal/70 mb-2">
              <KeyRound size={14} className="text-heritage" /> Choose {info?.is_reset ? 'a new' : 'a'} password — at least 10 characters.
            </div>
            <Field label="New password" type="password" value={password} onChange={setPassword} testid="new-password" required />
            <Field label="Confirm password" type="password" value={confirm} onChange={setConfirm} testid="confirm-password" required />
            {submitError && <div className="text-sm text-red-700" data-testid="set-password-error">{submitError}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="set-password-submit"
              className="w-full bg-heritage text-base font-semibold rounded-full py-3 hover:bg-charcoal disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {busy ? 'Setting password\u2026' : (<><Check size={16} /> Set password and sign in</>)}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">
        {label}{required && <span className="text-heritage ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-heritage"
        autoComplete="new-password"
      />
    </div>
  );
}

function formatErr(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).filter(Boolean).join(' ');
  return 'Sorry, something went wrong. Please try again.';
}
